import {ContentSection} from '@sollapay/ui/components';
import {ListExportMenu} from '@sollapay/web-core';
import {useCallback, useRef, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ErrorAlertMessage} from '@/components';
import {useCanCreateBuyer} from '@/features/buyers/hooks';
import {AddBuyerFlow} from '@/features/buyers/modals';
import {
  showBeneficiaryAddedToast,
  showBeneficiaryRemovedToast,
  showBeneficiaryUpdatedToast
} from '@/features/trust-accounts/components';
import {
  useBeneficiariesQuery,
  useCreateBeneficiaryMutation,
  useDeleteBeneficiaryMutation,
  useUpdateBeneficiaryMutation
} from '@/features/trust-accounts/hooks';
import {
  useCurrentTrustAccount,
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {BeneficiariesTabContentSkeleton} from './BeneficiariesTabContent.skeleton';
import {BeneficiariesTable} from './BeneficiariesTable';
import {createBeneficiaryExportColumns} from './beneficiary-export.config';

import type {BeneficiaryRow, DraftBeneficiary} from './types/beneficiary-draft.types';
import type {PartyRole} from '@sollapay/enums';
import type {CreateBeneficiaryPayload} from '@sollapay/types';
import type {FC} from 'react';

/** ~4s deferred-deletion window — matches showBeneficiaryRemovedToast's own display duration. */
const REMOVAL_UNDO_WINDOW_MS = 4_000;

function mapToRow(
  b: NonNullable<ReturnType<typeof useBeneficiariesQuery>['data']>[number],
  addableRoles: PartyRole[]
): BeneficiaryRow {
  return {
    name: b.fullName,
    role: b.role,
    entityType: b.entityType,
    idNumber: b.identifier,
    idType: b.idType,
    idCountry: b.idCountry,
    date: b.birthOrIncorporationDate,
    address: b.address,
    source: addableRoles.includes(b.role) ? 'manual' : 'deal',
    partyId: b.id
  };
}

/** At most one inline row can be open at a time — a union makes that invariant structural. */
type RowMode = {type: 'none'} | {type: 'add'} | {type: 'edit'; partyId: string};

export const BeneficiariesTabContent: FC = () => {
  const {
    id: trustAccountId,
    refId: trustAccountRefId,
    name: trustAccountName,
    trustPurpose
  } = useCurrentTrustAccount();
  const beneficiariesQuery = useBeneficiariesQuery(trustAccountId);
  const {t, i18n} = useTranslation('trustAccounts');
  const {canCreateBeneficiary, canUpdateBeneficiary, canDeleteBeneficiary} =
    useTrustAccountPermissions();
  const canCreateBuyer = useCanCreateBuyer();
  const {inlineAddableBeneficiaryRoles, showsBeneficiaryAddCta} = useTrustTypeStrategy();
  const openAddBuyerRef = useRef<(() => void) | null>(null);
  const [rowMode, setRowMode] = useState<RowMode>({type: 'none'});
  const [pendingRemovalIds, setPendingRemovalIds] = useState<Set<string>>(new Set());
  const removalTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const createBeneficiaryMutation = useCreateBeneficiaryMutation(trustAccountId);
  const updateBeneficiaryMutation = useUpdateBeneficiaryMutation(trustAccountId);
  const deleteBeneficiaryMutation = useDeleteBeneficiaryMutation(trustAccountId);
  const isInlineAddMode = inlineAddableBeneficiaryRoles.length > 0;

  const beneficiaries = useMemo(
    () =>
      (beneficiariesQuery.data ?? [])
        .map(b => mapToRow(b, inlineAddableBeneficiaryRoles))
        .filter(row => !row.partyId || !pendingRemovalIds.has(row.partyId)),
    [beneficiariesQuery.data, pendingRemovalIds, inlineAddableBeneficiaryRoles]
  );

  const handleAddParty = useCallback(() => {
    if (isInlineAddMode) {
      updateBeneficiaryMutation.reset();
      setRowMode({type: 'add'});
      return;
    }
    openAddBuyerRef.current?.();
  }, [isInlineAddMode, updateBeneficiaryMutation]);

  const handleConfirmDraft = useCallback(
    (draft: DraftBeneficiary) => {
      if (!draft.role) return;
      createBeneficiaryMutation.mutate(
        {
          fullName: draft.name,
          role: draft.role as CreateBeneficiaryPayload['role'],
          entityType: draft.entityType,
          identifier: draft.idNumber,
          birthOrIncorporationDate: draft.date,
          address: draft.address
        },
        {
          onSuccess: () => {
            setRowMode({type: 'none'});
            showBeneficiaryAddedToast();
          }
        }
      );
    },
    [createBeneficiaryMutation]
  );

  const handleCancelDraft = useCallback(() => {
    setRowMode({type: 'none'});
    createBeneficiaryMutation.reset();
  }, [createBeneficiaryMutation]);

  const handleStartEdit = useCallback(
    (row: BeneficiaryRow) => {
      if (!row.partyId) return;
      createBeneficiaryMutation.reset();
      setRowMode({type: 'edit', partyId: row.partyId});
    },
    [createBeneficiaryMutation]
  );

  const handleConfirmEdit = useCallback(
    (partyId: string, values: DraftBeneficiary) => {
      if (!values.role) return;
      updateBeneficiaryMutation.mutate(
        {
          partyId,
          payload: {
            fullName: values.name,
            role: values.role as CreateBeneficiaryPayload['role'],
            entityType: values.entityType,
            identifier: values.idNumber,
            birthOrIncorporationDate: values.date,
            address: values.address
          }
        },
        {
          onSuccess: () => {
            setRowMode({type: 'none'});
            showBeneficiaryUpdatedToast();
          }
        }
      );
    },
    [updateBeneficiaryMutation]
  );

  const handleCancelEdit = useCallback(() => {
    setRowMode({type: 'none'});
    updateBeneficiaryMutation.reset();
  }, [updateBeneficiaryMutation]);

  const restoreRemovedRow = useCallback((partyId: string) => {
    const timer = removalTimers.current.get(partyId);
    if (timer) {
      clearTimeout(timer);
      removalTimers.current.delete(partyId);
    }
    setPendingRemovalIds(prev => {
      const next = new Set(prev);
      next.delete(partyId);
      return next;
    });
  }, []);

  const handleRemoveRow = useCallback(
    (partyId: string) => {
      setPendingRemovalIds(prev => new Set(prev).add(partyId));
      const timer = setTimeout(() => {
        removalTimers.current.delete(partyId);
        deleteBeneficiaryMutation.mutate(partyId, {
          onError: () => restoreRemovedRow(partyId)
        });
      }, REMOVAL_UNDO_WINDOW_MS);
      removalTimers.current.set(partyId, timer);
      showBeneficiaryRemovedToast(() => restoreRemovedRow(partyId));
    },
    [deleteBeneficiaryMutation, restoreRemovedRow]
  );

  const beneficiaryExportColumns = useMemo(
    () => createBeneficiaryExportColumns(t, i18n.language, inlineAddableBeneficiaryRoles),
    [t, i18n.language, inlineAddableBeneficiaryRoles]
  );

  if (beneficiariesQuery.isLoading) {
    return <BeneficiariesTabContentSkeleton />;
  }

  const canAddParty = isInlineAddMode
    ? canCreateBeneficiary
    : showsBeneficiaryAddCta && canCreateBuyer;
  const activeMutationError = [
    createBeneficiaryMutation,
    updateBeneficiaryMutation,
    deleteBeneficiaryMutation
  ].find(mutation => mutation.isError)?.error;

  return (
    <ContentSection
      title={t('view.beneficiaries.title')}
      subtitle={t('view.beneficiaries.subtitle')}
      action={
        <ListExportMenu<BeneficiaryRow>
          rows={beneficiaries}
          columns={beneficiaryExportColumns}
          refId={trustAccountRefId}
          entity="beneficiaries"
          sheetName={trustAccountName}
          disabled={beneficiaries.length === 0}
        />
      }
    >
      <AddBuyerFlow
        trustAccountId={trustAccountId}
        trigger={onOpen => {
          openAddBuyerRef.current = onOpen;
          return null;
        }}
      />
      {activeMutationError && (
        <ErrorAlertMessage
          error={activeMutationError}
          trustPurpose={trustPurpose}
          className="mb-4"
        />
      )}
      <BeneficiariesTable
        beneficiaries={beneficiaries}
        onAddParty={canAddParty ? handleAddParty : undefined}
        onStartEdit={canUpdateBeneficiary ? handleStartEdit : undefined}
        onRemoveRow={canDeleteBeneficiary ? handleRemoveRow : undefined}
        addableRoles={inlineAddableBeneficiaryRoles}
        isAddingParty={rowMode.type === 'add'}
        isConfirmingDraft={createBeneficiaryMutation.isPending}
        onConfirmDraft={handleConfirmDraft}
        onCancelDraft={handleCancelDraft}
        editingPartyId={rowMode.type === 'edit' ? rowMode.partyId : null}
        isConfirmingEdit={updateBeneficiaryMutation.isPending}
        onConfirmEdit={handleConfirmEdit}
        onCancelEdit={handleCancelEdit}
      />
    </ContentSection>
  );
};
